import {
  type DayOfEmergencyDto,
  type DayOfEmergencyTimelineEntryDto,
  type EmergencyDeclarationEndKind,
  fetchDayOfEmergencyAnonymousAccessBatchSetSameStatusForRecipient,
  fetchDayOfEmergencyAnonymousAccessEndEmergencyDeclarationForRecipient,
  fetchDayOfEmergencyAnonymousAccessGetBubbleContextForRecipient,
  fetchDayOfEmergencyAnonymousAccessGetDayForRecipient,
  fetchDayOfEmergencyAnonymousAccessListActiveEmergencyDeclarationsForRecipient,
  fetchDayOfEmergencyAnonymousAccessListAdminContactsForRecipient,
  fetchDayOfEmergencyAnonymousAccessListMapLocationsForRecipient,
  fetchDayOfEmergencyAnonymousAccessListParticipantsForRecipient,
  fetchDayOfEmergencyAnonymousAccessListTimelineForRecipient,
  fetchDayOfEmergencyAnonymousAccessPatchParticipantForRecipient,
  fetchDayOfEmergencyAnonymousAccessValidateRecipient,
} from "@crowdcoursing/api/openapi";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { useOperationalMapRealtime } from "~hooks/useOperationalMapRealtime";
import { useToast } from "~hooks/useToast";
import {
  LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER,
  postRecipientRequestSchoolwideSafetyStatusSms,
} from "~services/lawEnforcementEmergencyCommandCenterApi";
import { useBackendUrl } from "~services/providers/BackendUrlProvider";
import { recipientCommandCenterQueryKeys } from "~services/recipientCommandCenterQueryKeys";
import { ActiveEmergencyDeclarationsSection } from "~ui/organisms/ActiveEmergencyDeclarationsSection/ActiveEmergencyDeclarationsSection";
import { EmergencyMapReadOnly } from "~ui/organisms/EmergencyMapReadOnly/EmergencyMapReadOnly";
import { ParticipantRosterCard } from "~ui/organisms/ParticipantRosterCard/ParticipantRosterCard";
import { RecipientEmergencyChat } from "~ui/organisms/RecipientEmergencyChat/RecipientEmergencyChat";

import { LawEnforcementBubbleContextSection } from "../LawEnforcementCommandCenter/LawEnforcementBubbleContextSection";
import {
  EVERYONE_SUMMARY_GROUPS,
  KNOWN_STATUS_VALUES,
  OTHER_LEGACY_SUMMARY_ID,
  participantDisplayName,
  participantMatchesSummaryFilter,
  type ParticipantRow,
  rosterMatchesSearch,
  STATUS_OPTIONS,
  statusLabelForKey,
  TIMELINE_ENTRY_KINDS,
  timelineEntryMatchesKindFilter,
  timelineKindLabel,
} from "../VisitorManagement/emergencyCommandCenterShared";
import {
  EmergencyParticipantStatusDialog,
  MY_LOCATION_MAP_VALUE,
} from "../VisitorManagement/EmergencyParticipantStatusDialog";

type TabKey = "everyone" | "report" | "timeline" | "chat" | "map";

type BulkGeoState = "idle" | "prompting" | "granted" | "denied" | "unavailable";

export function RecipientCommandCenterPage() {
  const { dayOfEmergencyId = "", token = "" } = useParams<{ dayOfEmergencyId: string; token: string }>();
  const theme = useTheme();
  const { backendUrl } = useBackendUrl();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>("everyone");
  const [statusKey, setStatusKey] = useState<string>("safe");
  const [mapLocationId, setMapLocationId] = useState<string>("");
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkSelectedUserIds, setBulkSelectedUserIds] = useState<string[]>([]);
  const [batchApplyPending, setBatchApplyPending] = useState(false);
  const [rosterSearch, setRosterSearch] = useState("");
  const [summaryFilterIds, setSummaryFilterIds] = useState<string[]>([]);
  const [timelineKindFilterIds, setTimelineKindFilterIds] = useState<string[]>([]);
  const [participantDialog, setParticipantDialog] = useState<ParticipantRow | null>(null);
  const [participantDialogError, setParticipantDialogError] = useState<string | null>(null);
  const [participantDialogSaving, setParticipantDialogSaving] = useState(false);
  const [includeEmergencyContactsSms, setIncludeEmergencyContactsSms] = useState(false);
  const [requestAllStatusesPending, setRequestAllStatusesPending] = useState(false);
  const [dismissError, setDismissError] = useState<string | null>(null);
  const [dismissingDeclarationId, setDismissingDeclarationId] = useState<string | null>(null);
  const [bulkGeoState, setBulkGeoState] = useState<BulkGeoState>("idle");
  const [bulkUserCoords, setBulkUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const reportMapDefaultAppliedRef = useRef(false);

  const hasRoute = Boolean(dayOfEmergencyId && token);
  const statusDialogOpen = Boolean(participantDialog);

  const validationQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessValidateRecipient(
        {
          baseUrlOverride: backendUrl,
          body: { token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: recipientCommandCenterQueryKeys.validate(dayOfEmergencyId, token),
    retry: false,
  });

  const dismissibleDeclarationIds = validationQuery.data?.dismissibleDeclarationIds ?? [];

  const dayQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessGetDayForRecipient(
        {
          baseUrlOverride: backendUrl,
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: recipientCommandCenterQueryKeys.day(dayOfEmergencyId, token),
    refetchInterval: 30_000,
    retry: false,
  });

  const dayId = dayQuery.data?.id;
  const hasDay = Boolean(dayId);

  const participantsQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute && hasDay),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessListParticipantsForRecipient(
        {
          baseUrlOverride: backendUrl,
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: recipientCommandCenterQueryKeys.participants(dayOfEmergencyId, token),
    refetchInterval: tab === "everyone" || tab === "report" || tab === "map" ? 10_000 : false,
  });

  const timelineQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute && hasDay) && tab === "timeline",
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessListTimelineForRecipient(
        {
          baseUrlOverride: backendUrl,
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: recipientCommandCenterQueryKeys.timeline(dayOfEmergencyId, token),
    refetchInterval: tab === "timeline" ? 15_000 : false,
    retry: false,
  });

  const activeDeclarationsQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessListActiveEmergencyDeclarationsForRecipient(
        {
          baseUrlOverride: backendUrl,
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: recipientCommandCenterQueryKeys.declarations(dayOfEmergencyId, token),
    refetchInterval: 15_000,
  });

  const bubbleContextQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessGetBubbleContextForRecipient(
        {
          baseUrlOverride: backendUrl,
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: recipientCommandCenterQueryKeys.bubbleContext(dayOfEmergencyId, token),
    refetchInterval: 60_000,
    retry: false,
  });

  const adminContactsQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessListAdminContactsForRecipient(
        {
          baseUrlOverride: backendUrl,
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: recipientCommandCenterQueryKeys.adminContacts(dayOfEmergencyId, token),
    refetchInterval: 60_000,
    retry: false,
  });

  const recipientMapLocationsQuery = useQuery({
    enabled: Boolean(backendUrl && hasRoute && hasDay && (statusDialogOpen || tab === "report" || tab === "map")),
    queryFn: ({ signal }) =>
      fetchDayOfEmergencyAnonymousAccessListMapLocationsForRecipient(
        {
          baseUrlOverride: backendUrl,
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        },
        signal,
      ),
    queryKey: [...recipientCommandCenterQueryKeys.root, "map-locations", dayOfEmergencyId, token] as const,
    refetchInterval: tab === "map" ? 60_000 : false,
    retry: false,
  });

  const rosterRows = useMemo(() => (participantsQuery.data ?? []) as ParticipantRow[], [participantsQuery.data]);

  const bulkFilteredRows = useMemo(
    () => rosterRows.filter((p) => rosterMatchesSearch(p, bulkSearch)),
    [rosterRows, bulkSearch],
  );

  useEffect(() => {
    const valid = new Set(rosterRows.map((p) => String(p.userId)));
    setBulkSelectedUserIds((prev) => prev.filter((id) => valid.has(id)));
  }, [rosterRows]);

  useEffect(() => {
    if (tab !== "report") {
      reportMapDefaultAppliedRef.current = false;
    }
  }, [tab]);

  useEffect(() => {
    if (tab !== "report" || !hasDay) {
      return;
    }
    if (!navigator.geolocation) {
      setBulkGeoState("unavailable");
      return;
    }
    setBulkGeoState("prompting");
    setBulkUserCoords(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBulkUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setBulkGeoState("granted");
      },
      () => {
        setBulkGeoState("denied");
        setBulkUserCoords(null);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 25_000 },
    );
  }, [hasDay, tab]);

  useEffect(() => {
    if (bulkGeoState !== "denied" && bulkGeoState !== "unavailable") {
      return;
    }
    setMapLocationId((prev) => (prev === MY_LOCATION_MAP_VALUE ? "" : prev));
  }, [bulkGeoState]);

  useEffect(() => {
    if (tab !== "report" || !hasDay) {
      return;
    }
    if (bulkGeoState !== "granted" || !bulkUserCoords || reportMapDefaultAppliedRef.current) {
      return;
    }
    if (mapLocationId !== "") {
      reportMapDefaultAppliedRef.current = true;
      return;
    }
    setMapLocationId(MY_LOCATION_MAP_VALUE);
    reportMapDefaultAppliedRef.current = true;
  }, [bulkGeoState, bulkUserCoords, hasDay, mapLocationId, tab]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const o of STATUS_OPTIONS) {
      counts[o.value] = 0;
    }
    let otherUnknownStatuses = 0;
    for (const raw of rosterRows) {
      const key = raw.statusKey ?? "not-reported";
      if (KNOWN_STATUS_VALUES.has(key)) {
        counts[key] += 1;
      } else {
        otherUnknownStatuses += 1;
      }
    }
    return { counts, otherUnknownStatuses };
  }, [rosterRows]);

  const toggleSummaryFilter = useCallback((id: string) => {
    setSummaryFilterIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const toggleTimelineKindFilter = useCallback((kind: string) => {
    setTimelineKindFilterIds((prev) => (prev.includes(kind) ? prev.filter((x) => x !== kind) : [...prev, kind]));
  }, []);

  const toggleBulkUser = useCallback((userId: string) => {
    setBulkSelectedUserIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  }, []);

  const clearBulkSelection = useCallback(() => {
    setBulkSelectedUserIds([]);
  }, []);

  const handleSubmitBulkSameStatus = useCallback(async () => {
    if (!backendUrl || !dayOfEmergencyId || bulkSelectedUserIds.length === 0) {
      return;
    }
    setBatchApplyPending(true);
    try {
      if (mapLocationId === MY_LOCATION_MAP_VALUE) {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error("Location is not supported in this browser."));
            return;
          }
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: 25_000,
          });
        });
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        for (const uid of bulkSelectedUserIds) {
          await fetchDayOfEmergencyAnonymousAccessPatchParticipantForRecipient({
            baseUrlOverride: backendUrl,
            body: {
              bubbleMapLocationId: null,
              reportedLatitude: lat,
              reportedLongitude: lng,
              statusKey,
            },
            headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
            pathParams: { dayOfEmergencyId, userId: uid },
          });
        }
      } else {
        await fetchDayOfEmergencyAnonymousAccessBatchSetSameStatusForRecipient({
          baseUrlOverride: backendUrl,
          body: {
            bubbleMapLocationId: mapLocationId ? mapLocationId : null,
            statusKey,
            userIds: bulkSelectedUserIds,
          },
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId },
        });
      }
      const n = bulkSelectedUserIds.length;
      toast.success(n === 1 ? "Updated 1 participant." : `Updated ${n} participants.`);
      setBulkSelectedUserIds([]);
      await queryClient.invalidateQueries({
        queryKey: recipientCommandCenterQueryKeys.participants(dayOfEmergencyId, token),
      });
      await queryClient.invalidateQueries({
        queryKey: recipientCommandCenterQueryKeys.timeline(dayOfEmergencyId, token),
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update selected participants.");
    } finally {
      setBatchApplyPending(false);
    }
  }, [backendUrl, bulkSelectedUserIds, dayOfEmergencyId, mapLocationId, queryClient, statusKey, toast, token]);

  const filteredRoster = useMemo(() => {
    return rosterRows
      .filter((p) => rosterMatchesSearch(p, rosterSearch))
      .filter((p) => participantMatchesSummaryFilter(p, summaryFilterIds));
  }, [rosterRows, rosterSearch, summaryFilterIds]);

  const timelineEntriesNewestFirst = useMemo(() => {
    const rows = timelineQuery.data ?? [];
    return [...rows].sort((a, b) => {
      const ta = new Date(a.occurredAtUtc ?? 0).getTime();
      const tb = new Date(b.occurredAtUtc ?? 0).getTime();
      return tb - ta;
    });
  }, [timelineQuery.data]);

  const filteredTimelineEntries = useMemo(() => {
    return timelineEntriesNewestFirst.filter((e) => timelineEntryMatchesKindFilter(e.kind, timelineKindFilterIds));
  }, [timelineEntriesNewestFirst, timelineKindFilterIds]);

  const loadFailed = dayQuery.isError;
  const unauthorized =
    dayQuery.error &&
    typeof dayQuery.error === "object" &&
    "status" in dayQuery.error &&
    (dayQuery.error as { status: number }).status === 401;

  const invalidateAll = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: recipientCommandCenterQueryKeys.root });
  }, [queryClient]);

  const handleRequestAllStatuses = useCallback(async () => {
    if (!backendUrl || !dayOfEmergencyId || !token) {
      return;
    }
    setRequestAllStatusesPending(true);
    try {
      const res = await postRecipientRequestSchoolwideSafetyStatusSms(
        backendUrl,
        dayOfEmergencyId,
        token,
        includeEmergencyContactsSms,
      );
      toast.success(
        `SMS: ${res.smsSendAttemptCount ?? 0} sent to phones (${res.distinctUserCount ?? 0} people in scope). ${res.smsSkippedNoPhoneCount ?? 0} skipped with no phone on file.`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send schoolwide status request.");
    } finally {
      setRequestAllStatusesPending(false);
    }
  }, [backendUrl, dayOfEmergencyId, includeEmergencyContactsSms, token, toast]);

  const handleDismissDeclaration = useCallback(
    async (declarationId: string, endKind: EmergencyDeclarationEndKind) => {
      if (!backendUrl) {
        return;
      }
      setDismissError(null);
      setDismissingDeclarationId(declarationId);
      try {
        await fetchDayOfEmergencyAnonymousAccessEndEmergencyDeclarationForRecipient({
          baseUrlOverride: backendUrl,
          body: { endKind },
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId, declarationId },
        });
        await queryClient.invalidateQueries({ queryKey: recipientCommandCenterQueryKeys.root });
      } catch (e) {
        const status = typeof e === "object" && e !== null && "status" in e ? (e as { status: number }).status : 0;
        if (status === 403) {
          setDismissError("You are not allowed to dismiss this emergency.");
        } else {
          setDismissError(e instanceof Error ? e.message : "Could not dismiss this emergency.");
        }
      } finally {
        setDismissingDeclarationId(null);
      }
    },
    [backendUrl, dayOfEmergencyId, queryClient, token],
  );

  const handleSaveParticipantStatus = useCallback(
    async (payload: {
      bubbleMapLocationId: string | null;
      statusKey: string;
      reportedLatitude: number | null;
      reportedLongitude: number | null;
    }) => {
      if (!backendUrl || !participantDialog?.userId) {
        return;
      }
      setParticipantDialogSaving(true);
      setParticipantDialogError(null);
      try {
        await fetchDayOfEmergencyAnonymousAccessPatchParticipantForRecipient({
          baseUrlOverride: backendUrl,
          body: {
            bubbleMapLocationId: payload.bubbleMapLocationId,
            reportedLatitude: payload.reportedLatitude,
            reportedLongitude: payload.reportedLongitude,
            statusKey: payload.statusKey,
          },
          headers: { [LAW_ENFORCEMENT_ACCESS_TOKEN_HEADER]: token },
          pathParams: { dayOfEmergencyId, userId: String(participantDialog.userId) },
        });
        await queryClient.invalidateQueries({
          queryKey: recipientCommandCenterQueryKeys.participants(dayOfEmergencyId, token),
        });
        await queryClient.invalidateQueries({
          queryKey: recipientCommandCenterQueryKeys.timeline(dayOfEmergencyId, token),
        });
        setParticipantDialog(null);
      } catch (e) {
        setParticipantDialogError(e instanceof Error ? e.message : "Could not update status.");
      } finally {
        setParticipantDialogSaving(false);
      }
    },
    [backendUrl, dayOfEmergencyId, participantDialog?.userId, queryClient, token],
  );

  const invalidateOperationalMapQueries = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: recipientCommandCenterQueryKeys.participants(dayOfEmergencyId, token),
    });
    await queryClient.invalidateQueries({
      queryKey: recipientCommandCenterQueryKeys.timeline(dayOfEmergencyId, token),
    });
    await queryClient.invalidateQueries({
      queryKey: [...recipientCommandCenterQueryKeys.root, "map-locations", dayOfEmergencyId, token],
    });
  }, [dayOfEmergencyId, queryClient, token]);

  useOperationalMapRealtime({
    accessToken: token,
    dayOfEmergencyId,
    enabled: hasRoute && hasDay,
    mode: "recipient",
    onParticipantStatusChanged: invalidateOperationalMapQueries,
  });

  if (!hasRoute) {
    return (
      <Box sx={{ boxSizing: "border-box", maxWidth: 720, minWidth: 0, mx: "auto", p: { md: 3, sm: 2, xs: 1 } }}>
        <Alert severity="warning">
          This link is missing required parameters. Use the full URL from your notification.
        </Alert>
      </Box>
    );
  }

  if (!backendUrl) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const showBulkMyLocationOption = bulkGeoState === "granted" && bulkUserCoords !== null;
  const bulkMapLocationSelectValue =
    mapLocationId === MY_LOCATION_MAP_VALUE && !showBulkMyLocationOption ? "" : mapLocationId;

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        maxWidth: "100%",
        minWidth: 0,
        mx: "auto",
        p: { md: 3, sm: 2, xs: 1 },
        width: "100%",
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        flexWrap="wrap"
        justifyContent="space-between"
        sx={{ gap: 1, mb: 2, minWidth: 0 }}
      >
        <Typography component="h1" sx={{ minWidth: 0 }} variant="h5">
          Emergency Command Center
        </Typography>
        {hasDay ? (
          <Button onClick={() => void invalidateAll()} size="small" variant="outlined">
            Refresh data
          </Button>
        ) : null}
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
        Opened via secure link. If you were notified as a recipient or as someone who may dismiss an alarm, you can view
        status and timeline here.
      </Typography>

      <LawEnforcementBubbleContextSection
        adminContactsQuery={adminContactsQuery}
        bubbleContextQuery={bubbleContextQuery}
      />

      {dismissError ? (
        <Alert onClose={() => setDismissError(null)} severity="error" sx={{ mb: 2 }}>
          {dismissError}
        </Alert>
      ) : null}

      <ActiveEmergencyDeclarationsSection
        declarations={activeDeclarationsQuery.data ?? []}
        dismissingDeclarationId={dismissingDeclarationId}
        dismissibleDeclarationIds={validationQuery.isSuccess ? dismissibleDeclarationIds : []}
        onDismissDeclaration={
          validationQuery.isSuccess && dismissibleDeclarationIds.length > 0 ? handleDismissDeclaration : undefined
        }
      />

      {hasDay && dayQuery.data ? (
        <Card sx={{ mb: 2, p: 2 }} variant="outlined">
          <Typography sx={{ mb: 1 }} variant="subtitle1">
            Request status from everyone
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
            Sends a text to each person with a role in this school with a short link to report their safety and
            location. Optionally include bubble admins and law enforcement contacts for this school.
          </Typography>
          <FormControlLabel
            control={
              <Switch checked={includeEmergencyContactsSms} onChange={(_, v) => setIncludeEmergencyContactsSms(v)} />
            }
            label="Include emergency contacts (bubble admins & law enforcement)"
          />
          <Box sx={{ mt: 2 }}>
            <Button
              disabled={requestAllStatusesPending}
              onClick={() => void handleRequestAllStatuses()}
              variant="contained"
            >
              {requestAllStatusesPending ? "Sending…" : "Request All Statuses"}
            </Button>
          </Box>
        </Card>
      ) : null}

      {dayQuery.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {loadFailed ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {unauthorized
            ? "This link is invalid or has expired. Use the link from your latest emergency notification."
            : "Could not load this emergency day. It may have been removed or the link may no longer be valid."}
        </Alert>
      ) : null}

      {hasDay && dayQuery.data ? (
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="caption">
          Date: {String((dayQuery.data as DayOfEmergencyDto).utcCalendarDate)}
        </Typography>
      ) : null}

      {hasDay ? (
        <>
          <Tabs
            allowScrollButtonsMobile
            onChange={(_, v) => setTab(v as TabKey)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            value={tab}
            variant="scrollable"
          >
            <Tab label="Everyone" value="everyone" />
            <Tab label="Report status" value="report" />
            <Tab label="Timeline" value="timeline" />
            <Tab label="Chat" value="chat" />
            <Tab label="Map" value="map" />
          </Tabs>

          {tab === "everyone" ? (
            <Stack spacing={2}>
              {participantsQuery.isLoading ? (
                <CircularProgress size={32} />
              ) : participantsQuery.isError ? (
                <Alert severity="error">Could not load participants.</Alert>
              ) : (
                <>
                  <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
                    <Typography color="text.secondary" component="span" variant="caption">
                      Click summary cards to filter the roster (select several to combine). Click again to remove a
                      filter.
                    </Typography>
                    {summaryFilterIds.length > 0 ? (
                      <Button onClick={() => setSummaryFilterIds([])} size="small" variant="text">
                        Clear status filters
                      </Button>
                    ) : null}
                  </Stack>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 1.5,
                      gridTemplateColumns: {
                        md: "repeat(4, minmax(0, 1fr))",
                        sm: "repeat(3, minmax(0, 1fr))",
                        xl: "repeat(6, minmax(0, 1fr))",
                        xs: "repeat(2, minmax(0, 1fr))",
                      },
                    }}
                  >
                    {EVERYONE_SUMMARY_GROUPS.map((g) => {
                      const selected = summaryFilterIds.includes(g.id);
                      return (
                        <Card
                          key={g.id}
                          variant="outlined"
                          sx={{
                            bgcolor: selected ? "action.selected" : undefined,
                            borderColor: selected ? "primary.main" : undefined,
                            borderWidth: selected ? 2 : 1,
                          }}
                        >
                          <CardActionArea
                            aria-label={`Filter by ${g.label}`}
                            aria-pressed={selected}
                            onClick={() => toggleSummaryFilter(g.id)}
                            sx={{ borderRadius: 1 }}
                          >
                            <CardContent sx={{ "&:last-child": { pb: 1.5 }, py: 1.5 }}>
                              <Typography color="text.secondary" component="div" variant="caption">
                                {g.label}
                              </Typography>
                              <Typography component="div" sx={{ mt: 0.5 }} variant="h5">
                                {g.statusValues.reduce((sum, v) => sum + (statusCounts.counts[v] ?? 0), 0)}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      );
                    })}
                    {statusCounts.otherUnknownStatuses > 0 ? (
                      <Card
                        key={OTHER_LEGACY_SUMMARY_ID}
                        variant="outlined"
                        sx={{
                          bgcolor: summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID) ? "action.selected" : undefined,
                          borderColor: summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID) ? "primary.main" : undefined,
                          borderWidth: summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID) ? 2 : 1,
                        }}
                      >
                        <CardActionArea
                          aria-label="Filter by other or legacy status"
                          aria-pressed={summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID)}
                          onClick={() => toggleSummaryFilter(OTHER_LEGACY_SUMMARY_ID)}
                          sx={{ borderRadius: 1 }}
                        >
                          <CardContent sx={{ "&:last-child": { pb: 1.5 }, py: 1.5 }}>
                            <Typography color="text.secondary" component="div" variant="caption">
                              Other / legacy
                            </Typography>
                            <Typography component="div" sx={{ mt: 0.5 }} variant="h5">
                              {statusCounts.otherUnknownStatuses}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    ) : null}
                  </Box>

                  <TextField
                    fullWidth
                    onChange={(e) => setRosterSearch(e.target.value)}
                    placeholder="Search by name, phone, or role"
                    size="small"
                    value={rosterSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Typography color="text.secondary" variant="caption">
                    Showing {filteredRoster.length} of {rosterRows.length} people
                    {rosterSearch.trim() || summaryFilterIds.length > 0 ? " (filtered)" : ""}
                  </Typography>

                  {filteredRoster.length === 0 && rosterRows.length > 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      No one matches your filters. Try a different search, adjust the status cards above, or clear
                      filters.
                    </Typography>
                  ) : null}

                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: {
                        md: "repeat(2, minmax(0, 1fr))",
                        xl: "repeat(4, minmax(0, 1fr))",
                        xs: "minmax(0, 1fr)",
                      },
                    }}
                  >
                    {filteredRoster.map((row) => (
                      <ParticipantRosterCard
                        key={row.userId}
                        participant={row}
                        onSelect={(p) => {
                          setParticipantDialog(p);
                          setParticipantDialogError(null);
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Stack>
          ) : null}

          {tab === "report" ? (
            <Stack maxWidth={640} spacing={2}>
              <Button
                disabled={bulkSelectedUserIds.length === 0 || batchApplyPending}
                onClick={() => void handleSubmitBulkSameStatus()}
                variant="contained"
              >
                {batchApplyPending
                  ? "Applying…"
                  : bulkSelectedUserIds.length === 0
                    ? "Apply status to selected users"
                    : `Apply status to ${bulkSelectedUserIds.length} user${bulkSelectedUserIds.length === 1 ? "" : "s"}`}
              </Button>
              <Typography variant="body2">
                Set status and optional map location, search the roster, select everyone who should receive this
                update—including yourself when your status should match—then apply. The Everyone tab updates after a
                successful save. When location access is allowed, <strong>My location (GPS)</strong> is selected by
                default and uses this device&apos;s coordinates for every selected person.{" "}
                <strong>My location (GPS)</strong> applies the same GPS position to all selected participants.
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="recipient-report-status-label">Status</InputLabel>
                <Select
                  label="Status"
                  labelId="recipient-report-status-label"
                  onChange={(e) => setStatusKey(String(e.target.value))}
                  value={statusKey}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="recipient-report-loc-label">Map location (optional)</InputLabel>
                <Select
                  label="Map location (optional)"
                  labelId="recipient-report-loc-label"
                  onChange={(e) => setMapLocationId(String(e.target.value))}
                  value={bulkMapLocationSelectValue}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {showBulkMyLocationOption ? (
                    <MenuItem value={MY_LOCATION_MAP_VALUE}>My location (GPS)</MenuItem>
                  ) : null}
                  {(recipientMapLocationsQuery.data ?? [])
                    .filter((l) => l.isActive)
                    .map((l) => (
                      <MenuItem key={l.id} value={String(l.id)}>
                        {l.label}
                      </MenuItem>
                    ))}
                </Select>
                {bulkGeoState === "prompting" ? (
                  <FormHelperText sx={{ alignItems: "center", display: "flex", gap: 1, mt: 1 }}>
                    <CircularProgress size={14} />
                    Requesting location access…
                  </FormHelperText>
                ) : null}
                {bulkGeoState === "denied" || bulkGeoState === "unavailable" ? (
                  <FormHelperText>
                    {bulkGeoState === "unavailable"
                      ? "This browser does not support location. Choose a published map location or None."
                      : "Location permission not granted. Choose a published map location or None."}
                  </FormHelperText>
                ) : null}
                {mapLocationId === MY_LOCATION_MAP_VALUE && showBulkMyLocationOption ? (
                  <FormHelperText>
                    Applies the same GPS coordinates (this device) to every selected participant.
                  </FormHelperText>
                ) : null}
              </FormControl>
              <TextField
                fullWidth
                InputProps={{
                  endAdornment:
                    bulkSearch.length > 0 ? (
                      <InputAdornment position="end">
                        <IconButton aria-label="Clear search" edge="end" onClick={() => setBulkSearch("")} size="small">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setBulkSearch(e.target.value)}
                placeholder="Search by name, phone, or role"
                size="small"
                value={bulkSearch}
              />
              <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
                <Button
                  disabled={bulkSelectedUserIds.length === 0}
                  onClick={clearBulkSelection}
                  size="small"
                  variant="text"
                >
                  Clear selection
                </Button>
                <Typography color="text.secondary" variant="caption">
                  {bulkSelectedUserIds.length} selected
                </Typography>
              </Stack>
              {participantsQuery.isLoading ? (
                <CircularProgress size={28} />
              ) : bulkFilteredRows.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No participants match your search.
                </Typography>
              ) : (
                <FormGroup
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    maxHeight: 320,
                    overflow: "auto",
                    p: 1,
                  }}
                >
                  {bulkFilteredRows.map((p) => {
                    const uid = String(p.userId ?? "");
                    if (!uid) {
                      return null;
                    }
                    const checked = bulkSelectedUserIds.includes(uid);
                    return (
                      <FormControlLabel
                        control={<Checkbox checked={checked} onChange={() => toggleBulkUser(uid)} size="small" />}
                        key={uid}
                        label={
                          <Box>
                            <Typography component="span" variant="body2">
                              {participantDisplayName(p)}
                            </Typography>
                            <Typography color="text.secondary" component="span" display="block" variant="caption">
                              {statusLabelForKey(p.statusKey)}
                            </Typography>
                          </Box>
                        }
                      />
                    );
                  })}
                </FormGroup>
              )}
              <Button
                disabled={bulkSelectedUserIds.length === 0 || batchApplyPending}
                onClick={() => void handleSubmitBulkSameStatus()}
                variant="contained"
              >
                {batchApplyPending
                  ? "Applying…"
                  : bulkSelectedUserIds.length === 0
                    ? "Apply status to selected users"
                    : `Apply status to ${bulkSelectedUserIds.length} user${bulkSelectedUserIds.length === 1 ? "" : "s"}`}
              </Button>
            </Stack>
          ) : null}

          {tab === "timeline" ? (
            <Stack spacing={2}>
              <Typography color="text.secondary" variant="body2">
                Activity for this reunification day (newest first): dispatches, status updates, and emergency plan
                access via shared links.
              </Typography>
              {timelineQuery.isLoading ? (
                <CircularProgress size={32} />
              ) : timelineQuery.isError ? (
                <Alert severity="error">Could not load timeline.</Alert>
              ) : timelineEntriesNewestFirst.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No recorded activity yet for this day.
                </Typography>
              ) : (
                <>
                  <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
                    <Typography color="text.secondary" component="span" variant="caption">
                      Filter by activity type (select one or more). Click again to clear a type.
                    </Typography>
                    {timelineKindFilterIds.length > 0 ? (
                      <Button onClick={() => setTimelineKindFilterIds([])} size="small" variant="text">
                        Clear type filters
                      </Button>
                    ) : null}
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {TIMELINE_ENTRY_KINDS.map((kind) => {
                      const selected = timelineKindFilterIds.includes(kind);
                      return (
                        <Chip
                          key={kind}
                          clickable
                          color={selected ? "primary" : "default"}
                          label={timelineKindLabel(kind)}
                          onClick={() => toggleTimelineKindFilter(kind)}
                          size="small"
                          variant={selected ? "filled" : "outlined"}
                        />
                      );
                    })}
                  </Stack>
                  <Typography color="text.secondary" variant="caption">
                    Showing {filteredTimelineEntries.length} of {timelineEntriesNewestFirst.length} activities
                    {timelineKindFilterIds.length > 0 ? " (filtered)" : ""}
                  </Typography>
                  {filteredTimelineEntries.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      No activities match the selected types. Clear a filter or choose different types.
                    </Typography>
                  ) : (
                    <Stack component="ol" sx={{ listStyle: "none", m: 0, p: 0 }} spacing={0}>
                      {filteredTimelineEntries.map((e: DayOfEmergencyTimelineEntryDto, i: number) => (
                        <Box
                          component="li"
                          key={`${e.kind}-${e.occurredAtUtc}-${i}`}
                          sx={{
                            "&:last-child": { pb: 0 },
                            borderColor: "divider",
                            borderLeft: 2,
                            ml: 0.5,
                            pb: 2,
                            pl: 2,
                          }}
                        >
                          <Typography color="text.secondary" variant="caption">
                            {new Date(e.occurredAtUtc ?? "").toLocaleString()}
                          </Typography>
                          <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                            <Chip label={timelineKindLabel(e.kind ?? "")} size="small" variant="outlined" />
                            {e.actorDisplayName ? (
                              <Typography color="text.secondary" variant="caption">
                                {e.actorDisplayName}
                              </Typography>
                            ) : null}
                          </Stack>
                          <Typography sx={{ mt: 1 }} variant="body2">
                            {e.summary}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          ) : null}

          {tab === "chat" ? (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                display: "flex",
                flexDirection: "column",
                height: "min(70vh, 560px)",
                minHeight: 360,
                overflow: "hidden",
              }}
            >
              {hasDay ? (
                <RecipientEmergencyChat
                  accessToken={token}
                  dayOfEmergencyId={dayOfEmergencyId}
                  highlightUserId={validationQuery.data?.userId}
                />
              ) : null}
            </Box>
          ) : null}

          {tab === "map" ? (
            <EmergencyMapReadOnly
              bubbleId={bubbleContextQuery.data?.bubbleId}
              error={recipientMapLocationsQuery.isError}
              highlightColor={theme.palette.primary.main}
              loading={recipientMapLocationsQuery.isLoading}
              locations={recipientMapLocationsQuery.data ?? []}
              participants={participantsQuery.data ?? []}
              siteName={bubbleContextQuery.data?.displayName ?? undefined}
            />
          ) : null}
        </>
      ) : null}

      <EmergencyParticipantStatusDialog
        errorMessage={participantDialogError}
        mapLocations={(recipientMapLocationsQuery.data ?? []).map((l) => ({
          id: String(l.id),
          isActive: l.isActive,
          label: l.label ?? "",
          latitude: l.latitude,
          longitude: l.longitude,
          polygonGeoJson: (l as { polygonGeoJson?: string | null }).polygonGeoJson ?? null,
        }))}
        mapLocationsLoading={recipientMapLocationsQuery.isLoading}
        onClose={() => {
          setParticipantDialog(null);
          setParticipantDialogError(null);
        }}
        onSave={handleSaveParticipantStatus}
        open={statusDialogOpen}
        participant={participantDialog}
        saving={participantDialogSaving}
        title="Update participant status"
      />
    </Box>
  );
}
